<template>
  <div
    :class="$style.container"
  >
    <HeadingMedium>
      Checkout Summary
    </HeadingMedium>

    <div :class="$style.checkoutSummaryBox">
      <div
        v-if="isALaCarte"
        :class="$style.pricingRow"
        data-test-id="CheckoutSummary__aLaCarteSummary"
      >
        <HeadingSmall :hasMargin="false">
          {{ subscription.displayName }}
        </HeadingSmall>
      </div>
      <div
        v-else
        :class="$style.pricingRow"
        data-test-id="CheckoutSummary__packageSummary"
      >
        <div :class="$style.flexCol">
          <HeadingSmall :hasMargin="false">
            {{ subscription.displayName }}
          </HeadingSmall>
          <ParagraphBody bodySize="small">
            {{ subscriptionTypeDisplayText }}
          </ParagraphBody>
        </div>
        <HeadingSmall
          data-test-id="CheckoutSummary__packagePrice"
          :hasMargin="false"
        >
          ${{ formattedPrice }}/user/mo
        </HeadingSmall>
      </div>

      <div
        v-if="isALaCarte || $hasFeatureFlag($featureFlags.purchaseAddOnsInProduct)"
        :class="$style.addOnsContainer"
      >
        <div
          v-for="addOn of addOns"
          :key="addOn.name"
          :class="$style.addOnRow"
        >
          <div data-test-id="CheckoutSummary__addOnName">
            {{ addOn.name }}
          </div>
          <div data-test-id="CheckoutSummary__addOnPrice">
            ${{ addOn.price }}/user/mo
          </div>
        </div>
      </div>

      <hr
        v-if="addOns.length > 0"
        :class="$style.dividingLine"
      >

      <div
        v-if="isALaCarte || $hasFeatureFlag($featureFlags.purchaseAddOnsInProduct)"
        :class="$style.pricingRow"
      >
        <HeadingExtraSmall :hasMargin="false">
          {{ itemizedTotalLabel }}
        </HeadingExtraSmall>
        <HeadingExtraSmall
          data-test-id="CheckoutSummary__alaCartePrice"
          :hasMargin="false"
        >
          ${{ formattedTotalPrice }}/user/mo
        </HeadingExtraSmall>
      </div>

      <div
        v-if="isAnnual"
        :class="$style.annualSummary"
        data-test-id="CheckoutSummary__annualSummary"
      >
        <div :class="$style.pricingRow">
          <HeadingExtraSmall
            data-test-id="CheckoutSummary__userCount"
            :hasMargin="false"
          >
            {{ userCount }} Users
          </HeadingExtraSmall>
          <HeadingExtraSmall
            data-test-id="CheckoutSummary__totalMonthlyCost"
            :hasMargin="false"
          >
            {{ displayMonthlyCost }}
          </HeadingExtraSmall>
        </div>

        <hr
          v-if="!isFree"
          :class="$style.dividingLine"
          data-test-id="CheckoutSummary__totalLine"
        >

        <div
          v-if="!isFree"
          :class="$style.pricingRow"
          data-test-id="CheckoutSummary__pricingRow"
        >
          <div :class="$style.flexCol">
            <HeadingSmall :hasMargin="false">
              Total Due
            </HeadingSmall>
            <ParagraphBody bodySize="small">
              {{ formattedTotalMonthlyCost }} x 12
            </ParagraphBody>
          </div>
          <HeadingSmall
            data-test-id="CheckoutSummary__totalAnnualCost"
            :hasMargin="false"
          >
            {{ formattedTotalAnnualCost }}/yr
          </HeadingSmall>
        </div>
      </div>
    </div>

    <ParagraphBody
      v-if="isAnnual"
      data-test-id="CheckoutSummary__annualInfo"
    >
      We will charge your credit card for the annual amount on the first of the month.
      Any additional users above your selected total will be charged on a month to
      month basis at the annual rate.
    </ParagraphBody>
    <ParagraphBody
      v-else
      data-test-id="CheckoutSummary__monthlyInfo"
    >
      <span :class="$style.yourAccount">
        Your account is
        <span :class="$style.free">Free</span>
      </span> until you add your 11th user. Once you add your 11th user,
      we will start billing you on the 1st of the month.
    </ParagraphBody>

    <CheckoutPackageRecommendation v-on="$listeners" />
  </div>
</template>

<script>
import {
  HeadingExtraSmall,
  HeadingMedium,
  HeadingSmall,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import { mapGetters } from 'vuex';
import CheckoutPackageRecommendation from 'client/Payments/newComponents/CheckoutPackageRecommendation.vue';
import SubscriptionConstants from 'client/Payments/pricingMatrix/SubscriptionConstants';
import Util from 'client/util/Util';
import getAddOnPrice from 'client/Payments/utils/getAddOnPrice';

const {
  SubscriptionTypes,
  getProductCodeByType,
} = SubscriptionConstants;

const pricingModule = 'PricingModule';

export default {
  name: 'CheckoutSummary',

  components: {
    CheckoutPackageRecommendation,
    HeadingExtraSmall,
    HeadingMedium,
    HeadingSmall,
    ParagraphBody,
  },

  Util,

  props: {
    subscription: {
      type: Object,
      required: true,
    },
  },

  computed: {
    ...mapGetters(pricingModule, {
      isAnnual: 'getIsAnnual',
      userCount: 'getUserCount',
    }),

    addOns() {
      return this.subscription
        .addOns
        .map((addOn) => ({
          name: addOn.displayName,
          price: getAddOnPrice(addOn, this.isAnnual, this.subscription.productCode),
        }));
    },

    displayMonthlyCost() {
      return this.isFree
        ? '11 or more users required.'
        : `${this.$options.Util.formatAsPrice(this.totalMonthlyCost)}/mo`;
    },

    formattedPrice() {
      return this.$options.Util.fixDecimals(this.price);
    },

    formattedTotalAnnualCost() {
      return this.$options.Util.formatAsPrice(this.totalAnnualCost);
    },

    formattedTotalMonthlyCost() {
      return this.$options.Util.formatAsPrice(this.totalMonthlyCost);
    },

    formattedTotalPrice() {
      return this.$options.Util.fixDecimals(this.totalPrice);
    },

    isALaCarte() {
      return this.subscription.productCode === getProductCodeByType(SubscriptionTypes.alaCarte);
    },

    isFree() {
      return this.userCount <= 10;
    },

    itemizedTotalLabel() {
      return this.isAnnual ? 'Total Price Per User' : 'Total Due';
    },

    price() {
      return this.isAnnual
        ? this.subscription.annualPrice
        : this.subscription.listPrice;
    },

    subscriptionTypeDisplayText() {
      return this.isAnnual ? 'Annual' : 'Monthly';
    },

    totalAnnualCost() {
      return this.totalMonthlyCost * 12;
    },

    totalMonthlyCost() {
      return this.totalPrice * this.userCount;
    },

    totalPrice() {
      const addOnPrice = this.addOns.reduce((total, addOn) => total + addOn.price, 0);
      const basePrice = this.isAnnual
        ? this.subscription.annualPrice
        : this.subscription.listPrice;
      return addOnPrice + basePrice;
    },
  },
};
</script>

<style scoped module>
.addOnsContainer {
  display: flex;
  flex-direction: column;
  gap: var(--jc-spacer-small);
}

.addOnRow {
  display: flex;
  justify-content: space-between;
}

.annualSummary {
  display: flex;
  flex-direction: column;
  gap: var(--jc-spacer);
}

.container {
  padding: 1.25rem var(--jc-spacer-large);
}

.flexCol {
  display: flex;
  flex-direction: column;
}

.free {
  font-weight: 800;
}

.checkoutSummaryBox {
  background-color: var(--jcPrimary50);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: var(--jc-spacer);
  margin-bottom: var(--jc-spacer);
  padding: var(--jc-spacer-large);
}

.pricingRow {
  display: flex;
  justify-content: space-between;
}

.dividingLine {
  border: 1px solid var(--jcGrey300);
  width: 100%;
}

.yourAccount {
  font-weight: 600;
}

.yourAccountIsFree {
  margin: var(--jc-spacer) 0;
}
</style>
